/**
 * SpecialistID — Product PDP modular bundle: INVENTORY / STOCK
 * Part of the custom-product-* set (split out of the legacy custom-product.js).
 *
 * Owns the stock/lead-time label, the location-level inventory lookup (Apps
 * Script API), the weekend shipping-cutoff check, and the qty/stock sync.
 *
 * Public API: SPID.manageStockDisplay(variant_id), SPID.quantityLogic().
 * Private: getProductInventoryByLocation, isAfterFriday3pmBeforeMonday, and the
 * per-variant inventory cache.
 *
 * LOAD ORDER: after custom-product-core.js + custom-product-data.js, before
 * custom-product.js.
 */
(function () {
  'use strict';

  const SPID = window.SPID;
  const CONFIG = SPID.config;
  const setAddToCartOutOfStock = SPID.setAddToCartOutOfStock;
  const getVariantId = SPID.getVariantId;
  const getVariantData = SPID.getVariantData;
  const getProductMetafield = SPID.getProductMetafield;
  const getVariantMetafield = SPID.getVariantMetafield;

  // Inventory lookup hardening: one request per product, shared in-flight promise,
  // sessionStorage cache (5 min), 6s timeout with 1 retry (fallback by ~13.5s), never a blank line.
  const INV_TTL_MS = 5 * 60 * 1000;
  // Normal answers take 1-2s, so the first attempt gives up early (3.5s) and the retry,
  // which usually lands on a fresh script run, gets more room (10s).
  const INV_TIMEOUTS_MS = [3500, 10000];
  const INV_RETRY_DELAYS = [500];
  const INV_CACHE_PREFIX = 'spid_inv_';

  const productInventoryMemory = {};   // productId -> { ts, product }
  const productInventoryInflight = {}; // productId -> Promise<product|null>

  function readInventoryCache(productId) {
    var entry = productInventoryMemory[productId];
    if (!entry) {
      try {
        var raw = sessionStorage.getItem(INV_CACHE_PREFIX + productId);
        if (raw) entry = JSON.parse(raw);
      } catch (e) { entry = null; }
    }
    if (entry && entry.product && entry.ts && (Date.now() - entry.ts) < INV_TTL_MS) {
      productInventoryMemory[productId] = entry;
      return entry.product;
    }
    return null;
  }

  function writeInventoryCache(productId, product) {
    var entry = { ts: Date.now(), product: product };
    productInventoryMemory[productId] = entry;
    try {
      sessionStorage.setItem(INV_CACHE_PREFIX + productId, JSON.stringify(entry));
    } catch (e) {}
  }

  // One attempt: resolves to the product object, or throws on timeout / bad body.
  function requestProductInventory(productId, timeoutMs) {
    var controller = new AbortController();
    var timer = setTimeout(function() { controller.abort(); }, timeoutMs);

    return fetch(CONFIG.API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain'
      },
      body: JSON.stringify({ productId: productId }),
      signal: controller.signal
    })
    .then(function(response) { return response.text(); })
    .then(function(text) {
      var data;
      try { data = JSON.parse(text); } catch (e) { throw new Error('Non-JSON response'); }
      if (!data || data.error || !data.product || !Array.isArray(data.product.variants)) {
        throw new Error('Unexpected response: ' + (data && data.error ? data.error : 'missing variants'));
      }
      return data.product;
    })
    .then(
      function(product) { clearTimeout(timer); return product; },
      function(error) { clearTimeout(timer); throw error; }
    );
  }

  function fetchProductInventory(productId) {
    var cached = readInventoryCache(productId);
    if (cached) return Promise.resolve(cached);
    if (productInventoryInflight[productId]) return productInventoryInflight[productId];

    var attempt = 0;
    function run() {
      var timeoutMs = INV_TIMEOUTS_MS[Math.min(attempt, INV_TIMEOUTS_MS.length - 1)];
      return requestProductInventory(productId, timeoutMs).catch(function(error) {
        if (attempt < INV_RETRY_DELAYS.length) {
          var delay = INV_RETRY_DELAYS[attempt++];
          return new Promise(function(resolve) { setTimeout(resolve, delay); }).then(run);
        }
        throw error;
      });
    }

    var promise = run()
      .then(function(product) {
        writeInventoryCache(productId, product);
        return product;
      })
      .catch(function(error) {
        console.warn('Inventory lookup failed:', error && error.message ? error.message : error);
        return null;
      })
      .then(function(product) {
        delete productInventoryInflight[productId];
        return product;
      });

    productInventoryInflight[productId] = promise;
    return promise;
  }

  // Always returns a Promise: the variant's inventory record, or null on failure.
  function getProductInventoryByLocation(variant_id) {
    var productId = SPID.state.productData?.id || document.querySelector('[name="product_id"]')?.value;
    if (!productId) return Promise.resolve(null);

    return fetchProductInventory(productId).then(function(product) {
      if (!product) return null;
      var found = product.variants.find(function(variant) {
        return variant.variantId == variant_id.toString();
      });
      if (!found) console.warn('Variant not found for variant_id:', variant_id);
      return found || null;
    });
  }

  // SPID-376: the lead time store data alone can give, with no stock lookup: an explicit
  // variant or product lead time, else 10-14 business days for made-to-order (custom) products.
  // Same precedence as manageStockDisplay. '' for anything else.
  function getKnownLeadTime(variant_id) {
    var variant_info = getVariantMetafield(variant_id, 'shipping', 'info');
    if (variant_info && variant_info !== '') return variant_info;
    var product_info = getProductMetafield('shipping', 'info');
    if (product_info && product_info !== '') return product_info;
    var is_custom = getProductMetafield('custom', 'is_custom');
    if (is_custom === true || is_custom === "1" || is_custom === 1) return 'Ships in 10-14 business days';
    return '';
  }

  // Shown when the lookup fails for a purchasable variant. Makes no "In stock"
  // claim (Shopify's total is not Doral stock) and keeps the green pill hidden.
  function renderStockFallback(variant_id, variant) {
    if (!variant || variant.available === false) return;

    // Simon (2026-10-12): when the live lookup fails, do not promise a ship time we do not
    // know, and never let the fallback reach an order - so the Lead Time property is cleared
    // (except for custom products and explicit lead times, see known_lead_time below).
    // The longer wording ran past the one-line stock line on phones, so per Simon use the short
    // one, and let it wrap rather than push the page sideways.
    var fallback_text = '<span style="white-space:normal;">Live stock check unavailable. Refresh to try again.</span>';

    // SPID-376: custom products (and any with an explicit lead time) keep a real lead time when
    // the lookup fails, because it does not depend on stock; everything else behaves as above.
    var known_lead_time = getKnownLeadTime(variant_id);

    document.querySelectorAll(".stock_label.available-2").forEach(el => el.classList.add("forcehidden"));
    document.querySelectorAll(".stock_label.available-3").forEach(el => {
      el.classList.add("bottom-padding");
      el.innerHTML = known_lead_time || fallback_text;
      el.title = known_lead_time || '';
    });
    const leadTimeInput = document.querySelector("#lead_time");
    if (leadTimeInput) leadTimeInput.value = known_lead_time;
  }

  function manageStockDisplay(variant_id, background) {
    if (!variant_id) {
      variant_id = getVariantId();
      if (!variant_id) return;
    }

    const isDiscontinued = getProductMetafield('status', 'discontinued');
    if (isDiscontinued && isDiscontinued !== 'false' && isDiscontinued !== false) {
      const label2 = document.querySelector(".stock_label.available-2");
      const label3 = document.querySelector(".stock_label.available-3");
      if (label2) label2.classList.add("forcehidden");
      if (label3) {
        label3.classList.add("bottom-padding");
        label3.innerHTML = '';
        label3.title = '';
      }
      return;
    }

    const qtySelector = document.querySelector('.quantity__input');
    const inputQty = parseInt(qtySelector?.value || '1');

    if (inputQty === 1) {
      const perItem = document.querySelector(".per-item-price");
      if (perItem) perItem.style.display = 'none';
    } else {
      const perItem = document.querySelector(".per-item-price");
      if (perItem) perItem.style.display = 'block';
    }

    // Get variant from JSON
    const variant = getVariantData(variant_id);

    // While the lookup runs, show a quiet placeholder instead of an empty gap.
    if (!background) {
      document.querySelectorAll(".stock_label.available-3").forEach(el => {
        if (el.textContent.trim() === '') { el.innerHTML = '<span class="spid-stock-checking" style="color:#8a8a85;font-style:normal;">Checking stock...</span>'; el.title = ''; }
      });
    }

    getProductInventoryByLocation(variant_id)
      .then(function(variantInventory) {
        if (!variantInventory) {
          // Background refreshes never blank or replace a line that is already painted.
          if (!background) renderStockFallback(variant_id, variant);
          return null;
        }

        if (variantInventory) {
          var doralQty = (variantInventory.inventoryLevels || {})["Specialist ID"] || 0;

          var inventory_policy = getVariantMetafield(variant_id, 'shipping', 'inventory_policy') || getProductMetafield('shipping', 'inventory_policy') || 'deny';

          // Get shipping and inventory info from metafields only
          //
          // is_custom is emitted into ProductJson under the `custom` namespace
          // (sections/main-product.liquid), and as a real boolean. This used to ask for
          // namespace `product` and compare against the string "1", so it was null on
          // every product and the custom branch below never ran - a custom order Doral
          // could not cover fell through to the total-inventory branch and promised
          // "1-2 business days" while the blank still had to be procured from Brady.
          var product_iscustom = getProductMetafield('custom', 'is_custom');
          var product_shipping_info = getProductMetafield('shipping', 'info');
          var variant_shipping_info = getVariantMetafield(variant_id, 'shipping', 'info');
          var product_oos_lead_time = getProductMetafield('shipping', 'oos_lead_time');
          var variant_oos_lead_time = getVariantMetafield(variant_id, 'shipping', 'oos_lead_time');
          var collection_shipping_info = getProductMetafield('collection', 'lead_times');
          var qty = variant?.inventory_quantity || 0;
          var collection_additional_lead_time = getProductMetafield('collection', 'additional_lead_times');
          var product_backordered_lead_time = getProductMetafield('shipping', 'backordered_lead_time');
          var variant_backordered_lead_time = getVariantMetafield(variant_id, 'shipping', 'backordered_lead_time');

          document.querySelectorAll(".stock_label.available-2").forEach(el => el.classList.add("forcehidden"));
          document.querySelectorAll(".stock_label.available-3").forEach(el => el.classList.add("bottom-padding"));

          var lead_time_text = "";

          if (doralQty + 1 > inputQty) {
            setAddToCartOutOfStock(false);
            if (variant_shipping_info && variant_shipping_info !== '') {
              lead_time_text = variant_shipping_info;
            } else if (product_shipping_info && product_shipping_info !== '') {
              lead_time_text = product_shipping_info;
            } else if (collection_shipping_info && collection_shipping_info !== '') {
              lead_time_text = collection_shipping_info;
            } else {
              if (qty > 0) {
                document.querySelectorAll(".stock_label.available-2").forEach(el => el.classList.remove("forcehidden"));
                document.querySelectorAll(".stock_label.available-3").forEach(el => el.classList.remove("bottom-padding"));
                lead_time_text = 'Same day shipping if ordered by 3pm EST';
              } else {
                lead_time_text = 'Usually ships within 24 hours';
              }

              if (isAfterFriday3pmBeforeMonday()) {
                lead_time_text = 'Ships on Monday';
              }
            }
          } else if ((product_iscustom === true || product_iscustom === "1" || product_iscustom === 1)
                     && (variant_shipping_info || product_shipping_info || collection_shipping_info)) {
            // Custom logo products never show a fixed 10-14 days (Patrick, 2026-10-23). When Doral can't
            // cover the quantity, use the same variant, product or collection lead time as when it can.
            setAddToCartOutOfStock(false);
            lead_time_text = variant_shipping_info || product_shipping_info || collection_shipping_info;
          } else if (variantInventory.inventoryQuantity + 1 > inputQty) {
            setAddToCartOutOfStock(false);
            lead_time_text = 'Usually ships within 1-2 business days';
          } else {
            if (inventory_policy === 'deny') {
              setAddToCartOutOfStock(true);

              var totalAvailable = variantInventory.inventoryQuantity || 0;
              var qtyInputForCart = document.querySelector('.quantity__input[data-quantity-variant-id="' + variant_id + '"]')
                                 || document.querySelector('.quantity__input');
              var cartQty = parseInt(qtyInputForCart && qtyInputForCart.getAttribute('data-cart-quantity')) || 0;
              var availableForUser = Math.max(0, totalAvailable - cartQty);

              if (availableForUser > 0) {
                lead_time_text =
                  'Only ' + availableForUser.toLocaleString('en-US') + ' left &middot; ' +
                  '<a href="#" class="set-qty-max" data-qty="' + availableForUser + '">Set to max</a> &middot; ' +
                  '<a href="/pages/contact-us">Contact us</a>';
              } else {
                lead_time_text =
                  'Out of stock. ' +
                  '<a href="/pages/contact-us">Contact us for more</a>';
              }
            } else {
              setAddToCartOutOfStock(false);

              if (variant_oos_lead_time && variant_oos_lead_time !== '') {
                lead_time_text = variant_oos_lead_time;
              } else if (product_oos_lead_time && product_oos_lead_time !== '') {
                lead_time_text = product_oos_lead_time;
              } else {
                if (variant_backordered_lead_time !== '') {
                  lead_time_text = variant_backordered_lead_time;
                } else if (product_backordered_lead_time !== '') {
                  lead_time_text = product_backordered_lead_time;
                } else {
                  if (variantInventory.inventoryQuantity > 0) {
                    lead_time_text = `${Number(variantInventory.inventoryQuantity).toLocaleString('en-US')} ship now, the rest in a few days`;
                  } else {
                    lead_time_text = 'Not in stock, usually ships in a few days';
                  }
                }
              }
            }
          }

          if (lead_time_text !== "") {
            document.querySelectorAll(".stock_label.available-3").forEach(el => { el.innerHTML = lead_time_text; el.title = el.textContent.trim(); });
            const leadTimeInput = document.querySelector("#lead_time");
            if (leadTimeInput) leadTimeInput.value = lead_time_text;
          }

          if (collection_additional_lead_time && collection_additional_lead_time !== '') {
            const svg = '<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20" height="20" viewBox="0 0 30 30"><path d="M15,3C8.373,3,3,8.373,3,15c0,6.627,5.373,12,12,12s12-5.373,12-12C27,8.373,21.627,3,15,3z M16,21h-2v-7h2V21z M15,11.5 c-0.828,0-1.5-0.672-1.5-1.5s0.672-1.5,1.5-1.5s1.5,0.672,1.5,1.5S15.828,11.5,15,11.5z"></path></svg>';
            const label3 = document.querySelector(".stock_label.available-3");
            if (label3) {
              label3.innerHTML = lead_time_text + '<span class="additional-lead-time" title="' + collection_additional_lead_time + '">' + svg + '</span>';
              label3.title = (label3.textContent.trim() + ' ' + collection_additional_lead_time).trim();
            }
          }
        }
      })
      .catch(function(error) {
        console.error('Error:', error);
      });
  }

  // Then run every minute (60000 ms); served from cache until the 5 min TTL lapses
  setInterval(() => {
    manageStockDisplay(null, true);
  }, 60000);

  function isAfterFriday3pmBeforeMonday() {
    // Get current time in store timezone using Intl API (handles DST automatically)
    const now = new Date();

    // Format the date in the store's timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: CONFIG.STORE_TIMEZONE,
      weekday: 'short',
      hour: 'numeric',
      hour12: false
    });

    // Get the parts of the formatted date
    const parts = formatter.formatToParts(now);
    const weekday = parts.find(part => part.type === 'weekday')?.value;
    const hour = parseInt(parts.find(part => part.type === 'hour')?.value || '0');

    // Check Friday 3 PM or later
    if (weekday === 'Fri' && hour >= CONFIG.CUTOFF_HOUR) {
      return true;
    }
    // Saturday any time
    if (weekday === 'Sat') {
      return true;
    }
    // Sunday any time
    if (weekday === 'Sun') {
      return true;
    }

    return false;
  }

  function quantityLogic() {
    var variant_id = getVariantId();
    if (!variant_id) return;

    const variant = getVariantData(variant_id);
    if (!variant) return;

    var qty = variant?.inventory_quantity || 0;
    var current_qty = parseInt(document.querySelector(".quantity__input")?.value) || 0;
    var inventory_policy = variant?.inventory_policy || 'deny';

    manageStockDisplay();

    if (inventory_policy === 'continue') return;

    const currentQtyEl = document.querySelector('span.current_qty');
    if (currentQtyEl) currentQtyEl.innerHTML = qty;
    document.querySelectorAll(".quantity_warning span.qty-warning").forEach(el => el.classList.add("hide"));

    if (current_qty > qty) {
      setTimeout(function() {
        const submitBtn = document.querySelector(".product-form__submit");
        const submitSpan = submitBtn?.querySelector('span');
        if (submitSpan) submitSpan.innerHTML = "Add to cart";
        if (submitBtn) submitBtn.disabled = false;
      }, 700);
    }
  }

  SPID.manageStockDisplay = manageStockDisplay;
  SPID.quantityLogic = quantityLogic;

  // Announce this module so main-product.liquid can leave it out safely.
  if (window.SPID && SPID.register) SPID.register('inventory');
})();
